import React, { useCallback, useEffect, useRef } from 'react';
import {
    Checkbox,
    CheckboxGroup,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    Input,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Textarea
} from '@chakra-ui/react';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { isApiErrorResponse } from 'ts/common/common';
import type { QuestionnaireFormValues } from 'ts/common/components/questionnaire/QuestionnairePageForm';
import { useTranslate } from 'ts/common/hooks';
import { multipleChoiceSeparator } from 'ts/common/questionnaire/common';

const TEXT_SHORT_MAX_LENGTH = 500;
const TEXT_LONG_MAX_LENGTH = 3000;
const AUTO_SAVE_DEBOUNCE_MS = 3000;

function serializeResponseValue(value: unknown): string | null {
    if (value === undefined || value === null) {
        return null;
    }

    if (typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        return value.filter((v) => !!v).join(multipleChoiceSeparator);
    }

    return String(value);
}

interface Props {
    onSave: (questionId: number, responseText: string | null) => Promise<unknown>;
    pageIndex: number;
    question: SpApi.Client.IQuestionnaireQuestion;
}

const QuestionnaireQuestionField: React.FC<Props> = ({ onSave, pageIndex, question }) => {
    const t = useTranslate('clientQuestionnaire');

    const {
        register,
        control,
        watch,
        getValues,
        formState: { errors }
    } = useFormContext<QuestionnaireFormValues>();

    const fieldName = `pages.${pageIndex}.${question.id}` as Path<QuestionnaireFormValues>;
    // The last value we know has been *successfully* persisted server-side. A failed save leaves
    // this unchanged, so the next blur/change for this field retries it instead of treating a
    // failed attempt as done and silently moving on.
    const savedValueRef = useRef<string | null>(
        serializeResponseValue(
            question.responseType === 'choices_checkboxes'
                ? question.responseText?.split(multipleChoiceSeparator) ?? []
                : question.responseText ?? ''
        )
    );
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const performSave = useCallback(
        (serializedValue: string | null) =>
            onSave(question.id, serializedValue).then((response) => {
                if (
                    response === undefined ||
                    (response !== null && !isApiErrorResponse(response))
                ) {
                    savedValueRef.current = serializedValue;
                }

                return response;
            }),
        [onSave, question.id]
    );

    const saveNow = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        const serializedValue = serializeResponseValue(getValues(fieldName));

        if (serializedValue === savedValueRef.current) {
            return;
        }

        performSave(serializedValue);
    }, [fieldName, getValues, performSave]);

    const saveDebounced = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        timeoutRef.current = setTimeout(() => {
            timeoutRef.current = null;
            // Re-reads the form's current value itself rather than trusting a value captured when
            // the timer was armed, so it always saves whatever's actually there by now.
            saveNow();
        }, AUTO_SAVE_DEBOUNCE_MS);
    }, [saveNow]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    const fieldError = errors?.pages?.[String(pageIndex)]?.[String(question.id)];

    const questionPromptLabelId = `client-questionnaire-q-${question.id}`;

    return (
        <FormControl
            key={question.id}
            isRequired={question.required}
            isInvalid={!!fieldError}
            paddingX={{ base: '16px', lg: '40px' }}
            paddingY="24px"
            boxSizing="border-box"
        >
            <FormLabel id={questionPromptLabelId} fontSize="16px" marginBottom="16px">
                {question.questionText}
            </FormLabel>
            {question.responseType === 'text_short' && (
                <>
                    <Input
                        type="text"
                        placeholder={question.responsePlaceholder ?? undefined}
                        maxLength={TEXT_SHORT_MAX_LENGTH}
                        {...((): ReturnType<typeof register> => {
                            const reg = register(fieldName, {
                                required: question.required ? t('fieldRequired') : false,
                                maxLength: {
                                    value: TEXT_SHORT_MAX_LENGTH,
                                    message: t('maxCharactersExceeded', {
                                        max: TEXT_SHORT_MAX_LENGTH
                                    })
                                }
                            });
                            return {
                                ...reg,
                                onBlur: async (e) => {
                                    await reg.onBlur(e);
                                    saveNow();
                                },
                                onChange: async (e) => {
                                    await reg.onChange(e);
                                    saveDebounced();
                                }
                            };
                        })()}
                    />
                    <FormHelperText fontSize="xs">
                        {t('characterCount', {
                            current: (watch(fieldName) as unknown as string)?.length ?? 0,
                            max: TEXT_SHORT_MAX_LENGTH
                        })}
                    </FormHelperText>
                </>
            )}
            {question.responseType === 'text_long' && (
                <>
                    <Textarea
                        variant="outline"
                        placeholder={question.responsePlaceholder ?? undefined}
                        minHeight="120px"
                        maxLength={TEXT_LONG_MAX_LENGTH}
                        {...((): ReturnType<typeof register> => {
                            const reg = register(fieldName, {
                                required: question.required ? t('fieldRequired') : false,
                                maxLength: {
                                    value: TEXT_LONG_MAX_LENGTH,
                                    message: t('maxCharactersExceeded', {
                                        max: TEXT_LONG_MAX_LENGTH
                                    })
                                }
                            });
                            return {
                                ...reg,
                                onBlur: async (e) => {
                                    await reg.onBlur(e);
                                    saveNow();
                                },
                                onChange: async (e) => {
                                    await reg.onChange(e);
                                    saveDebounced();
                                }
                            };
                        })()}
                    />
                    <FormHelperText fontSize="xs">
                        {t('characterCount', {
                            current: (watch(fieldName) as unknown as string)?.length ?? 0,
                            max: TEXT_LONG_MAX_LENGTH
                        })}
                    </FormHelperText>
                </>
            )}
            {question.responseType === 'choices_dropdown' && (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        required: question.required ? t('fieldRequired') : false
                    }}
                    render={({ field }) => (
                        <Select
                            ref={field.ref}
                            placeholder={question.responsePlaceholder ?? t('selectAnOption')}
                            value={(field.value as unknown as string) ?? ''}
                            name={fieldName}
                            onBlur={field.onBlur}
                            onChange={(e) => {
                                field.onChange(e.target.value);
                                saveNow();
                            }}
                            maxWidth="400px"
                        >
                            {question.choices.map((choice) => (
                                <option key={choice.id} value={choice.choiceText}>
                                    {choice.choiceText}
                                </option>
                            ))}
                        </Select>
                    )}
                />
            )}
            {question.responseType === 'choices_radio' && (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        required: question.required ? t('fieldRequired') : false
                    }}
                    render={({ field }) => (
                        <RadioGroup
                            value={(field.value as unknown as string) ?? ''}
                            onChange={(val) => {
                                field.onChange(val);
                                saveNow();
                            }}
                            aria-labelledby={questionPromptLabelId}
                        >
                            <Stack direction="column" spacing={2}>
                                {question.choices.map((choice) => (
                                    <Radio
                                        key={choice.id}
                                        value={choice.choiceText}
                                        name={fieldName}
                                        onBlur={field.onBlur}
                                    >
                                        {choice.choiceText}
                                    </Radio>
                                ))}
                            </Stack>
                        </RadioGroup>
                    )}
                />
            )}
            {question.responseType === 'choices_checkboxes' && (
                <Controller
                    name={fieldName}
                    control={control}
                    rules={{
                        validate: (value) =>
                            !question.required ||
                            (Array.isArray(value) && value.length > 0) ||
                            t('atLeastOneOptionRequired')
                    }}
                    render={({ field }) => (
                        <CheckboxGroup
                            value={(field.value as unknown as string[]) ?? []}
                            onChange={(values) => {
                                field.onChange(values);
                                saveNow();
                            }}
                            aria-labelledby={questionPromptLabelId}
                        >
                            <Stack direction="column" spacing={2}>
                                {question.choices.map((choice) => (
                                    <Checkbox
                                        key={choice.id}
                                        value={choice.choiceText}
                                        name={fieldName}
                                        onBlur={field.onBlur}
                                    >
                                        {choice.choiceText}
                                    </Checkbox>
                                ))}
                            </Stack>
                        </CheckboxGroup>
                    )}
                />
            )}
            <FormErrorMessage>{fieldError?.message}</FormErrorMessage>
        </FormControl>
    );
};

export default QuestionnaireQuestionField;
